import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import TimelineIcon from '@mui/icons-material/Timeline';
import Chart, { useChart } from '../chart';

// ---------------------------------------------------------------------------

const MAX_HISTORY = 120; // 10 minutes at 5s polling

function pmQuality(value) {
  if (value <= 12) return { label: 'Good', color: 'success' };
  if (value <= 35.4) return { label: 'Fair', color: 'warning' };
  return { label: 'Poor', color: 'error' };
}

function co2Quality(value) {
  if (value <= 800) return { label: 'Good', color: 'success' };
  if (value <= 1000) return { label: 'Fair', color: 'warning' };
  return { label: 'Poor', color: 'error' };
}

function formatTime(date) {
  return date.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

// ---------------------------------------------------------------------------

function ReadingCell({ label, value, unit, quality }) {
  return (
    <Grid item xs={6} sm={4} md={3}>
      <Box>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6">{value}{unit && <Typography component="span" variant="body2" color="text.secondary"> {unit}</Typography>}</Typography>
        {quality && <Chip label={quality.label} color={quality.color} size="small" />}
      </Box>
    </Grid>
  );
}

function ReadingsGrid({ readings }) {
  return (
    <>
      <Typography variant="overline" color="text.secondary">Mass Concentration</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <ReadingCell label="PM1.0" value={readings.pm1 ?? '—'} unit="µg/m³" />
        <ReadingCell label="PM2.5" value={readings.pm25} unit="µg/m³" quality={pmQuality(readings.pm25)} />
        <ReadingCell label="PM10" value={readings.pm10} unit="µg/m³" />
        <ReadingCell label="CO2" value={readings.co2} unit="ppm" quality={co2Quality(readings.co2)} />
      </Grid>
      <Typography variant="overline" color="text.secondary">Environment</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <ReadingCell label="Temperature" value={readings.temp} unit="°C" />
        <ReadingCell label="Humidity" value={readings.humidity} unit="%" />
        <ReadingCell label="VOC" value={readings.voc} unit="ppm" />
      </Grid>
      <Typography variant="overline" color="text.secondary">Particle Counts (per 100cm³)</Typography>
      <Grid container spacing={2}>
        <ReadingCell label=">0.3 µm" value={readings.pc03 ?? '—'} />
        <ReadingCell label=">0.5 µm" value={readings.pc05 ?? '—'} />
        <ReadingCell label=">1.0 µm" value={readings.pc10 ?? '—'} />
        <ReadingCell label=">2.5 µm" value={readings.pc25 ?? '—'} />
        <ReadingCell label=">5.0 µm" value={readings.pc50 ?? '—'} />
        <ReadingCell label=">10 µm" value={readings.pc100 ?? '—'} />
      </Grid>
    </>
  );
}

// ---------------------------------------------------------------------------

function ReadingsChart({ history }) {
  const labels = history.map((h) => formatTime(h.time));

  const sharedXaxis = {
    categories: labels,
    labels: {
      show: true,
      rotate: 0,
      hideOverlappingLabels: true,
      style: { fontSize: '10px' },
    },
    tickAmount: 6,
  };

  const sharedChart = {
    animations: { enabled: true, dynamicAnimation: { speed: 500 } },
    toolbar: { show: false },
  };

  // Particles chart — PM1.0 + PM2.5 + PM10 share left axis
  const pmOptions = useChart({
    chart: sharedChart,
    xaxis: sharedXaxis,
    yaxis: {
      title: { text: '\u00b5g/m\u00b3' },
      min: 0,
    },
    stroke: { width: 2, curve: 'smooth' },
    tooltip: { x: { show: true }, shared: true },
    legend: { position: 'top', horizontalAlign: 'center' },
  });

  const pmSeries = [
    { name: 'PM1.0', data: history.map((h) => h.pm1) },
    { name: 'PM2.5', data: history.map((h) => h.pm25) },
    { name: 'PM10', data: history.map((h) => h.pm10) },
  ];

  // Particle counts chart
  const pcOptions = useChart({
    chart: sharedChart,
    xaxis: sharedXaxis,
    yaxis: {
      title: { text: 'particles / 100cm\u00b3' },
      min: 0,
    },
    stroke: { width: 2, curve: 'smooth' },
    tooltip: { x: { show: true }, shared: true },
    legend: { position: 'top', horizontalAlign: 'center' },
  });

  const pcSeries = [
    { name: '>0.3\u00b5m', data: history.map((h) => h.pc03) },
    { name: '>0.5\u00b5m', data: history.map((h) => h.pc05) },
    { name: '>1.0\u00b5m', data: history.map((h) => h.pc10) },
    { name: '>2.5\u00b5m', data: history.map((h) => h.pc25) },
    { name: '>5.0\u00b5m', data: history.map((h) => h.pc50) },
    { name: '>10\u00b5m', data: history.map((h) => h.pc100) },
  ];

  // CO2 chart — single axis
  const co2Options = useChart({
    chart: sharedChart,
    xaxis: sharedXaxis,
    yaxis: {
      title: { text: 'ppm' },
      min: 0,
    },
    stroke: { width: 2, curve: 'smooth' },
    tooltip: { x: { show: true } },
    legend: { show: false },
    colors: ['#FF6B6B'],
  });

  const co2Series = [
    { name: 'CO2', data: history.map((h) => h.co2) },
  ];

  // Environment chart — temp + humidity share left axis
  const envOptions = useChart({
    chart: sharedChart,
    xaxis: sharedXaxis,
    yaxis: {
      title: { text: '\u00b0C / %' },
    },
    stroke: { width: 2, curve: 'smooth' },
    tooltip: { x: { show: true }, shared: true },
    legend: { position: 'top', horizontalAlign: 'center' },
  });

  const envSeries = [
    { name: 'Temperature', data: history.map((h) => h.temp) },
    { name: 'Humidity', data: history.map((h) => h.humidity) },
    { name: 'VOC', data: history.map((h) => h.voc) },
  ];

  if (history.length < 2) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4, color: 'text.secondary' }}>
        <Typography variant="body2">Waiting for more data points...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
        Mass Concentration (PM1.0 / PM2.5 / PM10)
      </Typography>
      <Chart type="line" series={pmSeries} options={pmOptions} height={180} />

      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
        CO2
      </Typography>
      <Chart type="line" series={co2Series} options={co2Options} height={150} />

      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
        Particle Counts by Size
      </Typography>
      <Chart type="line" series={pcSeries} options={pcOptions} height={200} />

      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
        Environment
      </Typography>
      <Chart type="line" series={envSeries} options={envOptions} height={180} />
    </Box>
  );
}

// ---------------------------------------------------------------------------

export default function SensorReadings({ serialClient, busyOperation }) {
  const [readings, setReadings] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [secondsAgo, setSecondsAgo] = useState(0);
  const [view, setView] = useState('grid');

  const pollRef = useRef(null);
  const tickRef = useRef(null);

  const addToHistory = useCallback((data) => {
    setHistory((prev) => {
      const entry = {
        time: new Date(),
        pm1: data.pm1 || 0,
        pm25: data.pm25,
        pm10: data.pm10,
        co2: data.co2,
        temp: data.temp,
        humidity: data.humidity,
        voc: data.voc,
        pc03: data.pc03 || 0,
        pc05: data.pc05 || 0,
        pc10: data.pc10 || 0,
        pc25: data.pc25 || 0,
        pc50: data.pc50 || 0,
        pc100: data.pc100 || 0,
      };
      const next = [...prev, entry];
      return next.length > MAX_HISTORY ? next.slice(-MAX_HISTORY) : next;
    });
  }, []);

  // Poll sensor readings every 5s
  useEffect(() => {
    const fetchReadings = async () => {
      if (busyOperation) return;
      try {
        const data = await serialClient.readings();
        setReadings(data);
        setLastUpdate(new Date());
        setError(null);
        addToHistory(data);
      } catch (_) {
        setError('Live readings require a firmware update');
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
    };

    fetchReadings();
    pollRef.current = setInterval(fetchReadings, 5000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [serialClient, busyOperation, addToHistory]);

  // Tick "seconds ago" counter every 1s
  useEffect(() => {
    tickRef.current = setInterval(() => {
      if (lastUpdate) {
        setSecondsAgo(Math.floor((Date.now() - lastUpdate.getTime()) / 1000));
      }
    }, 1000);

    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [lastUpdate]);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h6">Air Quality</Typography>
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={(_, v) => { if (v) setView(v); }}
              size="small"
            >
              <ToggleButton value="grid" sx={{ px: 1, py: 0.25 }}>
                <GridViewIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="chart" sx={{ px: 1, py: 0.25 }}>
                <TimelineIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {lastUpdate && (
            <Typography variant="caption" color="text.secondary">
              Updated: {secondsAgo}s ago
              {history.length > 0 && ` \u00b7 ${history.length} samples`}
            </Typography>
          )}
        </Box>

        {!readings && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {readings && view === 'grid' && <ReadingsGrid readings={readings} />}
        {readings && view === 'chart' && <ReadingsChart history={history} />}
      </CardContent>
    </Card>
  );
}

SensorReadings.propTypes = {
  serialClient: PropTypes.object.isRequired,
  busyOperation: PropTypes.string,
};
